import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import LoadingButton from '@mui/lab/LoadingButton';
import { CheckboxTreeSelect } from '../../../../CommonComponents/CheckboxTreeSelect/CheckboxTreeSelect';
import { CheckboxTreeNodeId } from '@opengov/components-checkbox-tree';
import { STATE_PUBLISHED } from '@opengov/stories-shared/constants';
import { IPublicationNavMenuStory } from '@opengov/stories-shared/types';

import React, { useMemo, useState } from 'react';

import { IOnlinePublicationMessages, ISelectMenuStoryOption } from '../../../../../../types';
import { orderNodes, getNodesWithStories } from '../../../../../utils';
import { useSetupOnlinePublicationUi } from '../../context/SetupOnlinePublicationUi.context';
import { useFolderHierarchyWithStories } from '../../hooks/useFolderHierarchyWithStories';
import { useSetStoriesForMenuId } from '../../hooks/useSetStoriesForMenuId';

import { styles } from './SelectStoryModal.styles';

export type ISelectStoryModalProps = {
  entityId: number;
  menuStories: IPublicationNavMenuStory[];
  messages: IOnlinePublicationMessages;
  publicationId: string;
  selectedMenuId: string;
  selectedMenuName: string;
  userId: number;
  onClose(): void;
};

const checkNodeHasSomeEnabledStories = (nodeList: ISelectMenuStoryOption[], hasEnabledStories: boolean): boolean => {
  let hasEnabledStoriesVal = hasEnabledStories;
  nodeList.forEach((node: ISelectMenuStoryOption) => {
    if (node?.children?.length) {
      hasEnabledStoriesVal = checkNodeHasSomeEnabledStories(node.children, hasEnabledStoriesVal);
    } else if (node.isStory && !node.disabled && !hasEnabledStoriesVal) {
      hasEnabledStoriesVal = true;
    }
  });
  return hasEnabledStoriesVal;
};

const disableSectionsWithDisabledStories = (sectionNodes: ISelectMenuStoryOption[]): ISelectMenuStoryOption[] => {
  const compileNodes = (nodeList) => {
    const hasEnabledStories = false;
    return nodeList.map((node: ISelectMenuStoryOption): ISelectMenuStoryOption => {
      if (node?.children?.length) {
        node.disabled = !checkNodeHasSomeEnabledStories(node.children, hasEnabledStories);
        node.children = compileNodes(node.children);
      }
      return node;
    });
  };
  return compileNodes(sectionNodes);
};

export const getSectionAndStoriesStructure = (publicationSections, selectedStories: string[] = []): ISelectMenuStoryOption[] => {
  const nodesWithStories = getNodesWithStories(publicationSections);
  const compileNodes = (nodeList): ISelectMenuStoryOption[] => {
    if (!nodeList) return [];
    return nodeList.map((node): ISelectMenuStoryOption => {
      const hasSubfolders = node.hasOwnProperty('subFolders');
      const newNode: ISelectMenuStoryOption = {
        id: node.id,
        displayName: node.title,
        children: [] as ISelectMenuStoryOption[],
        disabled: !(node?.state === STATE_PUBLISHED || hasSubfolders) || selectedStories.includes(node.id),
        isStory: !hasSubfolders,
        toggled: false,
        order: node.order
      };

      if (node.stories && node.stories.length > 0) {
        newNode.children = compileNodes(node.stories);
      }
      if (node.subFolders && node.subFolders.length > 0) {
        newNode.children = newNode.children?.length
          ? [...newNode.children, ...compileNodes(node.subFolders)]
          : compileNodes(node.subFolders);
      }
      return newNode;
    });
  };
  return disableSectionsWithDisabledStories(
    orderNodes(compileNodes(nodesWithStories)) as ISelectMenuStoryOption[]
  );
};

export const SelectStoryModal: React.FC<ISelectStoryModalProps> = (props) => {
  const { entityId, menuStories, messages, publicationId, selectedMenuId, selectedMenuName, userId, onClose } = props;
  const [selectedNodesIds, setSelectedNodesIds] = useState<CheckboxTreeNodeId[]>([]);
  const [toggledNodesIds, setToggledNodesIds] = useState<CheckboxTreeNodeId[]>([]);

  const folderHierarchy = useFolderHierarchyWithStories(
    { publicationId, entityId },
    messages.failureText
  );

  const { setStoriesForMenuId, isLoading: isSettingMenuStories } = useSetStoriesForMenuId({
    successTitle: messages.changesSaved,
    successBody: messages.changesSavedSuccessfully,
    errorTitle: messages.failedToUpdate,
    errorBody: messages.errorDescription
  });
  const { dispatch: uiDispatch } = useSetupOnlinePublicationUi();

  // Toggle the first section open the first time the hierarchy arrives. After
  // that, user toggles take over (`onNodeToggle` writes the same state).
  const sectionWithStories = useMemo<ISelectMenuStoryOption[]>(() => {
    const folders = folderHierarchy.data;
    if (!folders) return [];
    const selectedStoriesIds = menuStories?.map((story) => story.storyId) ?? [];
    return getSectionAndStoriesStructure(folders, selectedStoriesIds);
  }, [folderHierarchy.data, menuStories]);

  React.useEffect(() => {
    if (sectionWithStories.length && toggledNodesIds.length === 0) {
      setToggledNodesIds([sectionWithStories[0]?.id]);
    }
  }, [sectionWithStories, toggledNodesIds.length]);

  const confirmButtonAction = () => {
    void setStoriesForMenuId({
      publicationId,
      menuId: selectedMenuId,
      storyIds: selectedNodesIds,
      menuStories,
      userId
    }).then((ok) => {
      if (ok) {
        uiDispatch({ type: 'SET_UPDATE_PUBLICATION_PREVIEW', needsRefresh: true });
        onClose();
      }
    });
  };

  const getTitle = () => {
    return messages.addStories;
  };

  return (
    <Dialog open={true} onClose={onClose} maxWidth="sm" fullWidth data-test="select-story-modal" aria-labelledby="select-story-dialog-title">
      <DialogTitle id="select-story-dialog-title" sx={styles.dialogTitle}>
        {getTitle()}
        <IconButton aria-label="Close" onClick={onClose} sx={styles.closeButton}>
          <CloseIcon aria-hidden={true} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.dialogContent}>
        {selectedMenuName && (
          <Typography variant="body2" color="text.secondary">
            {selectedMenuName}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          {messages.addStoriesDesc}
        </Typography>
        <CheckboxTreeSelect
          data={sectionWithStories}
          selectedIds={selectedNodesIds as string[]}
          onChange={(ids) => setSelectedNodesIds(ids)}
          isLoading={folderHierarchy.isFetching}
          filterPlaceholder="Search"
          maxHeight={300}
        />
      </DialogContent>
      <DialogActions sx={styles.dialogActions}>
        <Button variant="outlined" color="secondary" size="small" data-test="select-story-modal-cancel-button" onClick={onClose}>
          {messages.cancel}
        </Button>
        <LoadingButton
          loading={isSettingMenuStories}
          disabled={!selectedNodesIds.length}
          variant="contained"
          size="small"
          data-test="select-story-modal-confirm-button"
          onClick={confirmButtonAction}
        >
          {messages.confirm}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
